
interface Product {
  id: string;
  name: string;
  category: string;
  subcategory: string;
  manufacturer: string;
  region: string;
  price: number;
  unit: string;
  image: string;
  description: string;
  specs: string[];
  inStock: boolean;
  rating: number;
  reviews: number;
}

interface ProductListItemProps {
  product: Product;
}

export function ProductListItem({ product }: ProductListItemProps) {
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('ko-KR').format(price);
  };

  const getRegionName = (region: string) => {
    const regions: Record<string, string> = {
      busan: '부산',
      boryeong: '보령',
      mokpo: '목포',
      yeosu: '여수',
      tongyeong: '통영',
      pohang: '포항',
      jeju: '제주',
    };
    return regions[region] || region;
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-amber-100/80 overflow-hidden hover:shadow-lg hover:shadow-amber-100/50 transition-all duration-300 group cursor-pointer">
      <div className="flex flex-col md:flex-row">
        {/* Product Image */}
        <div className="relative w-full md:w-80 h-64 md:h-auto bg-amber-50 overflow-hidden flex-shrink-0">
          <img
            src={product.image}
            alt={product.name}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
          />
          {!product.inStock && (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
              <span className="bg-red-500 text-white px-4 py-2 rounded-lg font-semibold text-sm whitespace-nowrap">
                품절
              </span>
            </div>
          )}
          <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-medium text-amber-800 whitespace-nowrap">
            {product.subcategory}
          </div>
        </div>

        {/* Product Info */}
        <div className="flex-1 p-6">
          <div className="flex flex-col h-full">
            {/* Header */}
            <div className="flex-1">
              {/* Manufacturer & Region */}
              <div className="flex items-center gap-3 mb-3">
                <span className="text-sm text-amber-700 font-medium bg-amber-50 px-3 py-1 rounded border border-amber-100 whitespace-nowrap">
                  {product.manufacturer}
                </span>
                <span className="text-sm text-gray-500 flex items-center gap-1 whitespace-nowrap">
                  <i className="ri-map-pin-line"></i>
                  {getRegionName(product.region)}
                </span>
                {/* Rating */}
                <div className="flex items-center gap-2 ml-auto">
                  <div className="flex items-center gap-1">
                    <i className="ri-star-fill text-amber-400"></i>
                    <span className="text-sm font-semibold text-gray-900">{product.rating}</span>
                  </div>
                  <span className="text-sm text-gray-500 whitespace-nowrap">
                    ({product.reviews}개 리뷰)
                  </span>
                </div>
              </div>

              {/* Product Name */}
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                {product.name}
              </h3>

              {/* Description */}
              <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                {product.description}
              </p>

              {/* Specs */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
                {product.specs.map((spec, index) => (
                  <p key={index} className="text-sm text-gray-600 flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-amber-500 mt-0.5"></i>
                    <span>{spec}</span>
                  </p>
                ))}
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between pt-4 border-t border-amber-100/60">
              <div>
                <p className="text-xs text-gray-500 mb-1">가격</p>
                <p className="text-2xl font-bold text-amber-700 whitespace-nowrap">
                  {formatPrice(product.price)}원
                  <span className="text-base text-gray-500 font-normal ml-2">/ {product.unit}</span>
                </p>
              </div>
              <div className="flex gap-3">
                <button className="px-5 py-3 border border-amber-400 text-amber-700 rounded-lg hover:bg-amber-50 transition-colors font-medium whitespace-nowrap flex items-center gap-2 cursor-pointer">
                  <i className="ri-heart-line"></i>
                  <span>관심</span>
                </button>
                <button className="px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg hover:from-amber-400 hover:to-orange-400 transition-all font-medium whitespace-nowrap flex items-center gap-2 shadow-sm shadow-amber-200 cursor-pointer">
                  <span>상세보기</span>
                  <i className="ri-arrow-right-line"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
